import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  selectIsWhatsAppInfobipLocalesEnabled,
  selectLocaleImprovementsEnabled,
} from 'core/userContext';
import { useQueryTemplateAndProjectLocales } from 'modules/campaigns/lib/hooks/useQueryTemplateAndProjectLocales';
import { useQueryTemplateLocales } from 'modules/campaigns/lib/hooks/useTemplateLocales';
import { Locale } from 'modules/domains/template';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { useLocaleSettingsContext } from '../LocaleSettingsContext';
import { LocalesQueryContext, LocalesQueryContextState } from './LocalesQueryContext';
import { WhatsAppMedium } from 'constants/messageMediums';

type LocalesQueryContextProviderProps = { templateId?: number };

/**
 * TODO: Once all customers are moved to new locales experience, clean up this component by removing the legacy locales code.
 */
export const LocalesQueryContextProvider: FC<
  React.PropsWithChildren<LocalesQueryContextProviderProps>
> = ({ children, ...rest }) => {
  const isLocaleImprovementsEnabled = useSelector(selectLocaleImprovementsEnabled);
  const isWhatsAppInfobipLocalesEnabled = useSelector(selectIsWhatsAppInfobipLocalesEnabled);

  const { locales: legacyLocales, isLoading: legacyLoading } = useLocaleSettingsContext();

  const { templateId: queryParamTemplateId = 0 } = useQueryParamContext();
  const templateId = rest.templateId || queryParamTemplateId;

  const templateLocalesQueryOptions = useMemo(
    () => ({
      skip: templateId === 0 || isLocaleImprovementsEnabled,
      variables: { id: templateId },
    }),
    [templateId, isLocaleImprovementsEnabled],
  );

  const {
    templateLocales,
    messageMedium,
    refetch: refetchLocales,
    isLoading: localesLoading,
  } = useQueryTemplateLocales(templateLocalesQueryOptions);

  const isWhatsApp = isWhatsAppInfobipLocalesEnabled && messageMedium === WhatsAppMedium.WhatsApp;

  const queryTemplateAndProjectLocalesResponse = useQueryTemplateAndProjectLocales({
    skip: templateId === 0 || (!isLocaleImprovementsEnabled && !isWhatsApp),
    variables: { templateId },
    fetchPolicy: 'no-cache',
  });

  let context: LocalesQueryContextState;

  if (isLocaleImprovementsEnabled || isWhatsApp) {
    const {
      errorMessage = '',
      hasError,
      isLoading,
      refetch: refetchNew,
      templateAndProjectLocales,
    } = queryTemplateAndProjectLocalesResponse;

    context = {
      errorMessage,
      hasError,
      isLoading,
      refetch: refetchNew,
      allProjectLocales: templateAndProjectLocales.allProjectLocales || [],
      activeTemplateLocales: templateAndProjectLocales.activeTemplateLocales || [],
      templateDefaultLocale: templateAndProjectLocales.templateDefaultLocale || ({} as Locale),
      activeWhatsAppTemplateLocales: templateAndProjectLocales.activeWhatsAppTemplateLocales || [],
      invalidWhatsAppLocales: templateAndProjectLocales.invalidWhatsAppLocales || [],
      validWhatsAppLocales: templateAndProjectLocales.validWhatsAppLocales || [],
    };
  } else {
    /** To be removed once all customers convert to new locales experience */
    const defaultLocale = legacyLocales?.[0];
    const activeTemplateLocales = legacyLocales?.filter((obj) =>
      templateLocales?.some((tempLocale: { tag: string }) => tempLocale.tag === obj.tag),
    );

    context = {
      errorMessage: '',
      hasError: false,
      isLoading: legacyLoading || localesLoading,
      refetch: refetchLocales,
      allProjectLocales: legacyLocales || [],
      activeTemplateLocales: defaultLocale ? [defaultLocale, ...activeTemplateLocales] : [],
      templateDefaultLocale: defaultLocale || {},
      activeWhatsAppTemplateLocales: [],
      invalidWhatsAppLocales: [],
      validWhatsAppLocales: [],
    };
  }

  return <LocalesQueryContext.Provider value={context}>{children}</LocalesQueryContext.Provider>;
};
